import { Modal, type ModalProps } from "../Modal/Modal.js";
import { Sheet } from "../Sheet/Sheet.js";
import { useMinWidth } from "../hooks/useBreakpoint.js";
import styles from "./FormDialog.module.scss";

export type FormDialogProps = ModalProps;

/**
 * A FORM that is a centred `Modal` on desktop and a bottom `Sheet` on a phone (ART-251). Same props as `Modal`,
 * so a form dialog converts by swapping the component name.
 *
 * Why a phone gets a sheet: a centred card with the soft keyboard up leaves the form covered and unscrollable
 * (reported on New Task). The sheet is anchored to the bottom edge, lifted above the keyboard by
 * `--keyboard-inset`, keeps its title and actions pinned while only the fields scroll between them, and keeps the
 * same fields — one form, two chromes. On the sheet the title is a visible heading (the sheet has no header of its own), the subtitle sits under it, and the footer's
 * actions SPLIT the row so a thumb reaches either.
 *
 * Desktop-only Modal props (`size`, `fitViewport`, `bodyLayout`, `titleVariant`, `className`) apply to the Modal
 * alone; a sheet is always full-width and always scrolls.
 */
export function FormDialog(props: FormDialogProps) {
  const isDesktop = useMinWidth("lg");
  // A form dialog holds a draft by definition, so it guards its exits by default: any edit makes a dismissal (the
  // grabber, scrim, Escape, a DialogCancel) ask "Discard changes?" first. A caller with its own notion passes `dirty`.
  const dirty = props.dirty ?? "auto";
  if (isDesktop) return <Modal {...props} dirty={dirty} />;

  const { title, subtitle, onClose, closeLabel, footer, bodyAs, onSubmit, hideHeader, children } = props;
  const body = (
    <>
      {!hideHeader && (
        <div className={styles.head}>
          <h2 className={styles.title}>{title}</h2>
          {subtitle && <p className={styles.subtitle}>{subtitle}</p>}
        </div>
      )}
      <div className={styles.body}>{children}</div>
      {footer && <div className={styles.actions} data-modal-foot="">{footer}</div>}
    </>
  );
  return (
    <Sheet title={title} onClose={onClose} closeLabel={closeLabel} dirty={dirty} scroll="inner">
      {bodyAs === "form" ? (
        <form className={styles.frame} onSubmit={onSubmit}>{body}</form>
      ) : (
        <div className={styles.frame}>{body}</div>
      )}
    </Sheet>
  );
}
